// The billing cards' shared look (Pat, Oct 7 2026: "I really like the card looks in the Work Order
// Billing Breakdown — how many days they're in it — have that similar view across all the platforms;
// and when you click it open it expands front and center with all the history of everything that has
// happened on it"). Used by Work Order Billing, Large Job Billing, the job's Billing tile and the PM
// dashboard's Bills tile.
//   <DaysPills stage facts />      the day counters, by where the bill sits
//   <BillHistoryWindow … />        the bill opened big, its whole history (GET /bills/history)
//   billStage(column)              any board's column / invoice status → to_bill | check | billed | paid
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiFetch } from '../services/apiClient';

const TERMS_DAYS = 30;
const pill = (bg, fg, edge) => ({ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 999, background: bg, color: fg, border: `1px solid ${edge}`, fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap' });
const GRAY = ['#f3f4f6', '#374151', '#e5e7eb'], AMBER = ['#fffbeb', '#b45309', '#fde68a'], RED = ['#fef2f2', '#b91c1c', '#fecaca'], GREEN = ['#f0fdf4', '#15803d', '#bbf7d0'];
const toTime = v => { if (!v) return null; const s = String(v); const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(s + 'T12:00:00') : new Date(s); return isNaN(d) ? null : d.getTime(); };
const daysSince = v => { const t = toTime(v); return t == null ? null : Math.max(0, Math.floor((Date.now() - t) / 86400000)); };
const daysWord = n => `${n} day${n === 1 ? '' : 's'}`;
const shortDate = v => { const t = toTime(v); return t == null ? '' : new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };

export function billStage(column) {
  const c = String(column || '');
  if (['complete_to_bill', 'draft'].includes(c)) return 'to_bill';
  if (['ready_to_send', 'ready'].includes(c)) return 'check';
  if (['billing_not_paid', 'sent'].includes(c)) return 'billed';
  if (['paid', 'closed'].includes(c)) return 'paid';
  return null;
}

// facts: { finished_at, created_at, prepared_at, sent_at, paid_at, last_followup_at, last_followup_kind, followup_count }
export function DaysPills({ stage, facts = {} }) {
  const out = [];
  if (stage === 'to_bill') {
    const n = daysSince(facts.finished_at || facts.created_at);
    if (n != null) out.push([`🧾 ${daysWord(n)} waiting to be billed`, n > 14 ? RED : n > 7 ? AMBER : GRAY]);
  } else if (stage === 'check') {
    const n = daysSince(facts.prepared_at || facts.created_at);
    if (n != null) out.push([`👀 ${daysWord(n)} waiting for a check`, n > 5 ? RED : n > 2 ? AMBER : GRAY]);
  } else if (stage === 'billed') {
    const n = daysSince(facts.sent_at || facts.prepared_at);
    if (n != null) {
      out.push([`📤 ${daysWord(n)} out`, GRAY]);
      const over = n - TERMS_DAYS;
      out.push(over > 0 ? [`⏰ ${daysWord(over)} overdue`, RED] : ['✓ inside terms', GREEN]);
      const f = daysSince(facts.last_followup_at);
      out.push(f != null
        ? [`${facts.last_followup_kind === 'call' ? '📞' : '✉️'} follow-up ${f === 0 ? 'today' : f + 'd ago'}${facts.followup_count > 1 ? ` · ${facts.followup_count}×` : ''}`, n > 30 && f > 14 ? AMBER : GRAY]
        : ['📣 no follow-up yet', n > TERMS_DAYS ? AMBER : GRAY]);
    }
  } else if (stage === 'paid') {
    const s = toTime(facts.sent_at), p = toTime(facts.paid_at);
    if (s != null && p != null) out.push([`💵 paid in ${daysWord(Math.max(0, Math.round((p - s) / 86400000)))}`, GREEN]);
    else if (p != null) out.push([`💵 paid ${shortDate(facts.paid_at)}`, GREEN]);
  }
  if (!out.length) return null;
  return <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 7 }}>{out.map(([t, c]) => <span key={t} style={pill(...c)}>{t}</span>)}</div>;
}

const KIND = {
  start: { icon: '🟢', tint: '#15803d' }, step: { icon: '✔️', tint: '#1e3a5f' }, sent: { icon: '📤', tint: '#c2410c' }, email: { icon: '✉️', tint: '#1d4ed8' },
  followup: { icon: '📣', tint: '#b45309' }, call: { icon: '📞', tint: '#0e7490' }, paid: { icon: '💵', tint: '#15803d' }, void: { icon: '⛔', tint: '#6b7280' },
  problem: { icon: '⚠️', tint: '#b91c1c' }, note: { icon: '•', tint: '#6b7280' },
};
const dayLabel = v => { const t = toTime(v); return t == null ? '' : new Date(t).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }); };
const timeLabel = v => { const s = String(v || ''); if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return ''; const t = toTime(v); return t == null ? '' : new Date(t).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }); };

// The bill opened big, front and centre: what it is, where it sits, and everything that happened to it.
// projectId → a work order's bill; invoiceId → one invoice / pay application. `head` is the card's face.
export function BillHistoryWindow({ projectId, invoiceId, title, sub, stage, facts, openHref, openLabel = 'Open the job ›', onClose, onNavigate }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    const q = invoiceId ? `invoice_id=${encodeURIComponent(invoiceId)}` : `project_id=${encodeURIComponent(projectId)}`;
    apiFetch(`/bills/history?${q}`).then(setData).catch(e => setErr(e?.error || 'Could not read the history.'));
  }, [projectId, invoiceId]);
  useEffect(() => { const k = e => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); }, [onClose]);
  const groups = [];
  (data?.events || []).forEach(e => { const d = dayLabel(e.at); const g = groups[groups.length - 1]; if (g && g.d === d) g.rows.push(e); else groups.push({ d, rows: [e] }); });
  return createPortal(
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 6200, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2vh 2vw' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', color: '#111827', borderRadius: 16, width: 1000, maxWidth: '100%', height: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 80px rgba(0,0,0,0.4)', borderTop: '6px solid #c2410c', overflow: 'hidden' }}>
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 26 }}>🧾</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 21, fontWeight: 800 }}>{title}</div>
              {sub && <div style={{ fontSize: 13.5, color: '#4b5563', marginTop: 2 }}>{sub}</div>}
            </div>
            {openHref && <button onClick={() => (onNavigate ? onNavigate(openHref) : window.location.assign(openHref))} style={{ padding: '9px 16px', borderRadius: 9, border: '1px solid #1e3a5f', background: '#1e3a5f', color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>{openLabel}</button>}
            <button onClick={onClose} title="Close" style={{ background: 'none', border: 'none', fontSize: 28, lineHeight: 1, color: '#6b7280', cursor: 'pointer' }}>×</button>
          </div>
          {stage && <DaysPills stage={stage} facts={facts || {}} />}
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px 24px' }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>History{data ? ` · ${data.events.length}` : ''} — newest first</div>
          {err && <div style={{ color: '#b91c1c', fontWeight: 700 }}>{err}</div>}
          {!data && !err && <div style={{ color: '#9ca3af' }}>Reading the history…</div>}
          {data && !data.events.length && <div style={{ color: '#9ca3af' }}>Nothing recorded on this bill yet.</div>}
          {groups.map(g => (
            <div key={g.d} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: '#374151', margin: '4px 0 6px' }}>{g.d}</div>
              <div style={{ borderLeft: '2px solid #e5e7eb', marginLeft: 9, paddingLeft: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {g.rows.map((e, i) => {
                  const k = KIND[e.kind] || KIND.note;
                  return (
                    <div key={i} style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: -25, top: 1, width: 20, textAlign: 'center', fontSize: 13, background: '#fff' }}>{k.icon}</span>
                      <div style={{ fontSize: 14, color: '#111827', lineHeight: 1.45 }}>
                        {e.who && <b>{e.who} </b>}<span style={{ color: k.tint === '#6b7280' ? '#374151' : k.tint, fontWeight: e.kind === 'note' ? 500 : 700 }}>{e.what}</span>
                      </div>
                      <div style={{ fontSize: 11.5, color: '#9ca3af' }}>{timeLabel(e.at)}{e.subject ? ` · “${e.subject}”` : ''}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  , document.body);
}
